import { Fragment, useState } from 'react'
import { Link as RouterLink } from 'react-router-dom'
import { Box, ButtonBase, Link, Stack, TableBody, TableCell, TableHead, TableRow, Typography } from '@mui/material'
import { ArrowUpRightIcon, ChevronDownIcon, ChevronRightIcon, PlayIcon } from 'lucide-react'

import { InfoTooltip } from 'components/feedback/InfoTooltip'
import { useReferrerState } from 'hooks'
import { ROUTES } from 'routes'
import type { WebsiteSession } from 'services'

import { formatDuration, referrerLabel, replayView, sessionTime } from '../websiteAnalyticsModel'
import { Cell, dateTime, EmptyRow, HeadCell, StatTable } from './shared'

const COLUMNS = 10

/**
 * VRG-689 — a session's time on site: the pages PostHog timed, summed. When a page has no
 * time (no `$pageleave`), the total is a floor and says so; when none has, "—". Never a 0
 * standing in for "unknown".
 */
const TimeCell = ({ session }: { session: WebsiteSession }) => {
  const { knownSeconds, unknownPages } = sessionTime(session.pages)
  if (session.pages === null)
    return (
      <Cell align="right">
        <InfoTooltip term="website-time-on-page" text="Page times could not be read from PostHog for this list.">
          <Box component="span" sx={{ color: 'text.secondary', cursor: 'help' }} tabIndex={0}>
            —
          </Box>
        </InfoTooltip>
      </Cell>
    )
  if (knownSeconds === null)
    return (
      <Cell align="right">
        <InfoTooltip term="website-time-on-page">
          <Box component="span" sx={{ color: 'text.secondary', cursor: 'help' }} tabIndex={0}>
            —
          </Box>
        </InfoTooltip>
      </Cell>
    )
  return (
    <Cell align="right">
      {unknownPages ? (
        <InfoTooltip
          term="website-time-on-page"
          text={`At least this long: ${unknownPages} page${unknownPages === 1 ? "'s" : "s'"} time is unknown (closed without PostHog recording the visitor leaving it).`}
        >
          <Box component="span" sx={{ cursor: 'help' }} tabIndex={0}>
            {formatDuration(knownSeconds)}+
          </Box>
        </InfoTooltip>
      ) : (
        formatDuration(knownSeconds)
      )}
    </Cell>
  )
}

const ReplayCell = ({ session }: { session: WebsiteSession }) => {
  const view = replayView(session.recording, Boolean(session.replayUrl))
  if (!view.show)
    return (
      <Cell align="right">
        {view.label ? (
          <InfoTooltip term={view.term} text={view.tooltip}>
            <Typography component="span" variant="body3" color="text.disabled" sx={{ cursor: 'help' }} tabIndex={0}>
              {view.label}
            </Typography>
          </InfoTooltip>
        ) : (
          '—'
        )}
      </Cell>
    )
  return (
    <Cell align="right">
      <Stack direction="row" spacing={1} alignItems="center" justifyContent="flex-end" sx={{ whiteSpace: 'normal' }}>
        {view.label ? (
          <InfoTooltip term={view.term} text={view.tooltip}>
            <Typography component="span" variant="body4" color="text.secondary" sx={{ cursor: 'help', display: 'inline-block', maxWidth: 64, lineHeight: 1.2, textAlign: 'right' }} tabIndex={0}>
              {view.label}
            </Typography>
          </InfoTooltip>
        ) : null}
        <InfoTooltip text={view.label ? view.tooltip : 'Open the recording in PostHog'}>
          <Link
            href={session.replayUrl ?? undefined}
            target="_blank"
            rel="noopener noreferrer"
            aria-label="Open the recording in PostHog"
            data-testid="replay-link"
          >
            <PlayIcon size={14} />
          </Link>
        </InfoTooltip>
      </Stack>
    </Cell>
  )
}

/** A path, capped and wrapped so a long one cannot widen the table (it already scrolls at 1280). */
const PathText = ({ path }: { path: string | null }) => (
  <Box component="span" sx={{ display: 'inline-block', width: 'max-content', maxWidth: 150, whiteSpace: 'normal', overflowWrap: 'break-word', verticalAlign: 'middle' }}>
    {path ?? '—'}
  </Box>
)

/** The pages of one session, each with its time. Shown under the row when expanded. */
const PagesRow = ({ session, live }: { session: WebsiteSession; live: boolean }) => {
  const pages = session.pages ?? []
  return (
    <TableRow>
      <TableCell colSpan={COLUMNS} sx={{ py: 2, bgcolor: 'action.hover' }}>
        {pages.length === 0 ? (
          <Typography variant="body3" color="text.secondary">
            {session.pages === null ? 'Pages could not be read from PostHog.' : 'No pageviews recorded in this session.'}
          </Typography>
        ) : (
          <Box
            // Capped and pinned left: the row spans every column, and the table scrolls
            // sideways inside its card at 1280, so an uncapped grid put the times off screen.
            sx={{ display: 'grid', gridTemplateColumns: 'auto minmax(0, 1fr) auto', columnGap: 4, rowGap: 1, maxWidth: 560, position: 'sticky', left: 0 }}
            aria-label="Pages in this session"
          >
            {pages.map((p, i) => {
              const last = i === pages.length - 1
              // The last page of a session that is live right now is not "unknown": they are on it.
              const onItNow = live && last && p.seconds === null
              return (
                <Fragment key={`${p.at}-${i}`}>
                  <Typography variant="body3" color="text.secondary" sx={{ whiteSpace: 'nowrap' }}>
                    {dateTime(p.at)}
                  </Typography>
                  <Typography variant="body3" sx={{ overflowWrap: 'anywhere' }}>
                    {p.path ?? '—'}
                  </Typography>
                  <Typography variant="body3" sx={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                    {p.seconds !== null ? (
                      formatDuration(p.seconds)
                    ) : onItNow ? (
                      <Box component="span" sx={{ color: 'text.secondary' }}>
                        on it now
                      </Box>
                    ) : (
                      <InfoTooltip term="website-time-on-page">
                        <Box component="span" sx={{ color: 'text.secondary', cursor: 'help' }} tabIndex={0}>
                          —
                        </Box>
                      </InfoTooltip>
                    )}
                  </Typography>
                </Fragment>
              )
            })}
          </Box>
        )}
      </TableCell>
    </TableRow>
  )
}

/**
 * One table for Live now and Visitor history — same row, two windows.
 *
 * The visitor column shows the email ONLY when the API sent one, which it does only for
 * platform admins (`presentSession`, server-side). For everyone else an identified
 * visitor reads "Identified (admins see who)", so a manager can see that a lead is on
 * the site without seeing who.
 *
 * VRG-689: Time (seconds on site, from PostHog's previous-pageview properties) and a Pages
 * count that opens the session's pages with the time on each. Replay shows only when PostHog
 * holds a recording (`recording`), and fails soft to Replay "may be unavailable".
 */
export const WebsiteSessionTable = ({
  sessions,
  emptyText,
  showStarted,
}: {
  sessions: WebsiteSession[]
  emptyText: string
  showStarted?: boolean
}) => {
  // §42 — the signups link hands back a way to return here with the range intact.
  const referrer = useReferrerState('Analytics')
  const [open, setOpen] = useState<Set<string>>(() => new Set())
  const toggle = (id: string) =>
    setOpen((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  return (
    <StatTable>
      <TableHead>
        <TableRow>
          <HeadCell>{showStarted ? 'Started' : 'Last seen'}</HeadCell>
          <HeadCell>Visitor</HeadCell>
          <HeadCell>Current page</HeadCell>
          <HeadCell>Landed on</HeadCell>
          <HeadCell>Source</HeadCell>
          <HeadCell>Location</HeadCell>
          <HeadCell>Device</HeadCell>
          <HeadCell align="right">Pages</HeadCell>
          <HeadCell align="right">
            Time
            <InfoTooltip term="website-time-on-page" />
          </HeadCell>
          <HeadCell align="right">Replay</HeadCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {sessions.length === 0 ? (
          <EmptyRow colSpan={COLUMNS} text={emptyText} />
        ) : (
          sessions.map((s) => {
            const expanded = open.has(s.sessionId)
            return (
              <Fragment key={s.sessionId}>
                <TableRow>
                  <Cell>{dateTime(showStarted ? s.startedAt : s.lastSeenAt)}</Cell>
                  <Cell>
                    {s.email ? (
                      <Link component={RouterLink} to={ROUTES.INTERNAL_ACCESS} state={referrer} underline="hover">
                        {s.email}
                      </Link>
                    ) : s.identified ? (
                      <Typography variant="body3" color="text.secondary">
                        Identified (admins see who)
                      </Typography>
                    ) : (
                      <Typography variant="body3" color="text.secondary">
                        Anonymous
                      </Typography>
                    )}
                    {s.personUrl ? (
                      <InfoTooltip text="Open the PostHog person">
                        <Link
                          href={s.personUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          sx={{ ml: 1, verticalAlign: 'middle' }}
                          aria-label="Open the PostHog person"
                        >
                          <ArrowUpRightIcon size={14} />
                        </Link>
                      </InfoTooltip>
                    ) : null}
                  </Cell>
                  <Cell>
                    <PathText path={s.currentPath} />
                  </Cell>
                  <Cell>
                    <PathText path={s.entryPath} />
                  </Cell>
                  <Cell>{s.utmSource ? `utm: ${s.utmSource}` : referrerLabel(s.referrer ?? '')}</Cell>
                  <Cell>{[s.city, s.country].filter(Boolean).join(', ') || '—'}</Cell>
                  <Cell>{s.device ?? '—'}</Cell>
                  <Cell align="right">
                    <ButtonBase
                      onClick={() => toggle(s.sessionId)}
                      aria-expanded={expanded}
                      aria-label={`${s.pageviews} pages. ${expanded ? 'Hide' : 'Show'} the time on each page`}
                      data-testid="session-pages-toggle"
                      sx={{ gap: 0.5, borderRadius: 1, px: 1, typography: 'body3', fontSize: 13 }}
                    >
                      {s.pageviews}
                      {expanded ? <ChevronDownIcon size={14} /> : <ChevronRightIcon size={14} />}
                    </ButtonBase>
                  </Cell>
                  <TimeCell session={s} />
                  <ReplayCell session={s} />
                </TableRow>
                {expanded ? <PagesRow session={s} live={!showStarted} /> : null}
              </Fragment>
            )
          })
        )}
      </TableBody>
    </StatTable>
  )
}
